import { useContext, useEffect, useState } from 'react'
import { Box, Button, Chip, Link, Stack, Tab, Tabs, Typography } from '@mui/material'
import { CalendarTodayOutlined, InfoOutlined } from '@mui/icons-material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { a11yProps } from '../../../../shared/utils/misc-shared-utils'
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import RegulationApplicabilityService from '../../../../shared/services/regulation-applicability/regulation-applicability.service'
import { IComplianceCalendarEntry, IRegulationApplicabilityResponse } from '../../../../shared/services/regulation-applicability/regulation-applicability.model'
import { CertificationsPanel } from '../../../presentation-components/reporting-components/certifications/certifications-panel'
import { criterionIcon, formatValue, statusChip } from '../../../presentation-components/reporting-components/regulation-status'
import { ComplianceCalendarDialog } from '../../../presentation-components/reporting-components/compliance-calendar-dialog'

const formatCalendarDate = (isoDate: string): string =>
  new Date(`${isoDate.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

// Applies first: the tab must answer "what applies to us" without scrolling.
const STATUS_PRIORITY: Record<string, number> = { 'applies': 0, 'may-apply': 1, 'insufficient-data': 2, 'not-applicable': 3 }

// Deadlines is the default tab (the "what's next" surface); ?tab=regulations deep-links straight
// to the verdicts, ?tab=certifications to the organisation's management-system certificates.
const tabFromParam = (value: string | null): number => {
  if (value === 'regulations') return 1
  if (value === 'certifications') return 2
  return 0
}

const calendarChipLabel = (entry: IComplianceCalendarEntry): string => {
  if (entry.source === 'period') return 'Your deadline'
  if (entry.source === 'certification') return `Certificate${entry.regulationShortLabel ? ` · ${entry.regulationShortLabel}` : ''}`
  return `Statutory${entry.regulationShortLabel ? ` · ${entry.regulationShortLabel}` : ''}${entry.regulationStatus === 'may-apply' ? ' · may apply' : ''}`
}

const daysUntilLabel = (isoDate: string): string => {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((new Date(`${isoDate.slice(0, 10)}T00:00:00`).getTime() - startOfToday.getTime()) / 86400000)
  // The backend drops past dates by the server's clock; a viewer's clock can disagree by a
  // day, so clamp rather than ever showing a negative count.
  if (days <= 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days} days`
}

// Dedicated view of every regulation the platform evaluates, applicable or not, with the
// criteria behind each verdict. The card on the Reporting page is the summary; this is the
// full breakdown, evaluated from the selected reporting period's figures.
export const ReportingRegulationsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<number>(tabFromParam(searchParams.get('tab')))
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const [response, setResponse] = useState<IRegulationApplicabilityResponse | null>(null)
  const [hasLoaded, setHasLoaded] = useState<boolean>(false)
  const [loadFailed, setLoadFailed] = useState<boolean>(false)
  const [fetchAttempt, setFetchAttempt] = useState<number>(0)
  const [calendarDialogOpen, setCalendarDialogOpen] = useState<boolean>(false)
  const regulationApplicabilityService = new RegulationApplicabilityService()

  const reportPeriodId = reportPeriodContext.lastSelectedReportId
  const isAdmin = userDetailsContext.userDetails?.roles?.includes('admin') ?? false
  const organisationId = userDetailsContext.userDetails?.organisation?.id
  // The route is admin and analyst only; both record certificates.
  const canEditCertifications = !!userDetailsContext.userDetails?.roles?.some((role) => role === 'admin' || role === 'analyst')

  // In-page navigation to ?tab= (from the Reporting card, the evidence page or an email link)
  // must switch the tab without a remount.
  useEffect(() => {
    setTab(tabFromParam(searchParams.get('tab')))
  }, [searchParams])

  useEffect(() => {
    if (!reportPeriodId) {
      setResponse(null)
      return
    }
    // Stale-response guard: a period switch mid-flight must not paint the old period's answer.
    // Clearing the response up front stops the previous period's verdicts staying painted
    // while the new evaluation is in flight.
    let cancelled = false
    setHasLoaded(false)
    setLoadFailed(false)
    setResponse(null)
    regulationApplicabilityService.getForPeriod(reportPeriodId)
      .then(result => { if (!cancelled) { setResponse(result); setHasLoaded(true) } })
      .catch(() => { if (!cancelled) { setResponse(null); setLoadFailed(true); setHasLoaded(true) } })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodId, fetchAttempt])

  const missingCompanyType = response ? !response.inputs.companyType : false
  const nonGbpPeriod = !!response?.currencyCode && response.currencyCode.toUpperCase() !== 'GBP'
  const missingFinancials = !!response && !nonGbpPeriod && (response.inputs.totalRevenue == null || response.inputs.balanceSheetTotal == null)
  const missingEmployees = !!response && response.inputs.employeesTotal == null

  return (
    <PageOuterContainer>
      <PageHeading
        title='Regulations'
        includeBackButton={true}
        backButtonLink='/reporting'
        backButtonText='Back to reporting'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Which UK reporting regulations apply to your organisation, evaluated from the selected
        reporting period&apos;s figures. This is guidance based on the figures you have entered, not legal advice.
      </Typography>
      {hasLoaded && !response && !loadFailed && (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          The evaluation is not available for the selected reporting period.
        </Typography>
      )}
      {hasLoaded && loadFailed && (
        <Stack direction='row' alignItems='center' spacing={2}>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            The evaluation could not be loaded.
          </Typography>
          <Button variant='outlined' size='small' onClick={() => setFetchAttempt(a => a + 1)}>Try again</Button>
        </Stack>
      )}
      {hasLoaded && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_, next: number) => setTab(next)} aria-label='regulations page tabs'>
            <Tab label='Deadlines' {...a11yProps(0)} />
            <Tab label='Applicable regulations' {...a11yProps(1)} />
            <Tab label='Certifications' {...a11yProps(2)} />
          </Tabs>
        </Box>
      )}
      <CustomTabPanel value={tab} index={1}>
      {response && (missingCompanyType || missingFinancials || missingEmployees) && (
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 3 }}>
          <InfoOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {!isAdmin && 'Ask an administrator to complete your company type, employee counts and financial figures to sharpen these results.'}
            {isAdmin && missingCompanyType && (
              <>
                <Link component='button' type='button' onClick={() => navigate('/settings/company-settings/overview')} sx={{ verticalAlign: 'baseline' }}>
                  Set your company type
                </Link>
                {missingFinancials || missingEmployees ? ' and ' : ' to sharpen these results.'}
              </>
            )}
            {isAdmin && missingFinancials && (
              <>
                <Link component='button' type='button' onClick={() => navigate(`/settings/company-settings/reporting-periods/${response.reportPeriodId}`)} sx={{ verticalAlign: 'baseline' }}>
                  {missingCompanyType ? 'add' : 'Add'} this period&apos;s revenue and balance sheet total
                </Link>
                {missingEmployees ? ' and ' : ' to sharpen these results.'}
              </>
            )}
            {isAdmin && missingEmployees && (
              <>
                <Link component='button' type='button' onClick={() => navigate(`/settings/company-settings/reporting-periods/${response.reportPeriodId}`)} sx={{ verticalAlign: 'baseline' }}>
                  {missingCompanyType || missingFinancials ? 'enter' : 'Enter'} your employee counts
                </Link>
                {' to sharpen these results.'}
              </>
            )}
          </Typography>
        </Stack>
      )}
      </CustomTabPanel>
      <CustomTabPanel value={tab} index={0}>
      {response && (
        <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', p: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between' spacing={1}>
            <Box>
              <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Deadlines</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Your reporting deadline, the statutory dates for regulations that apply or may apply to you, and the expiry and audit dates of the certificates you have recorded.
              </Typography>
            </Box>
            {(response.calendar?.length ?? 0) > 0 && (
              <Button variant='outlined' size='small' startIcon={<CalendarTodayOutlined />} onClick={() => setCalendarDialogOpen(true)}>
                Add to your calendar
              </Button>
            )}
          </Stack>
          {(response.calendar?.length ?? 0) === 0 && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1.5 }}>
              No upcoming deadlines. Set a reporting deadline on the period, record your certificates under Certifications, and statutory dates appear here when a regulation applies to you.
            </Typography>
          )}
          <Box sx={{ mt: (response.calendar?.length ?? 0) > 0 ? 1.5 : 0 }}>
            {(response.calendar || []).map(entry => (
              <Stack key={entry.key} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ py: 1, borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main' }}>
                <Typography variant='body2' sx={{ color: 'text.primary', minWidth: '150px', fontWeight: 600 }}>
                  {formatCalendarDate(entry.date)}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' sx={{ color: 'text.primary' }}>{entry.label}</Typography>
                </Box>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Chip
                    size='small'
                    variant='outlined'
                    sx={{ color: 'text.secondary' }}
                    label={calendarChipLabel(entry)}
                  />
                  <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {daysUntilLabel(entry.date)}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Box>
        </Box>
      )}
      </CustomTabPanel>
      <CustomTabPanel value={tab} index={1}>
      {response && (
        <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', p: 3, mb: 3 }}>
          <Typography variant='subtitle1' sx={{ color: 'text.primary', mb: 1.5 }}>At a glance</Typography>
          {[...response.regulations]
            .sort((a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9))
            .map(regulation => (
              <Stack key={regulation.key} direction='row' alignItems='center' spacing={1.5} sx={{ py: 0.75 }}>
                <Box sx={{ minWidth: '140px' }}>{statusChip(regulation.status)}</Box>
                <Link
                  component='button'
                  type='button'
                  onClick={() => document.getElementById(`regulation-section-${regulation.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  sx={{ color: 'text.primary', textAlign: 'left' }}
                >
                  {regulation.label}
                </Link>
              </Stack>
            ))}
        </Box>
      )}
      {response && [...response.regulations]
        .sort((a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9))
        .map(regulation => (
        <Box key={regulation.key} id={`regulation-section-${regulation.key}`} sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: regulation.status === 'applies' ? 'primary.main' : 'lightGrey.main', borderRadius: '10px', p: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between' spacing={1}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>{regulation.label}</Typography>
            {statusChip(regulation.status)}
          </Stack>
          {regulation.status === 'applies' && response.reportingDeadline && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
              Reporting deadline you set: {formatCalendarDate(response.reportingDeadline)}
            </Typography>
          )}
          <Box sx={{ mt: 1.5 }}>
            {regulation.reasons.map(reason => (
              <Stack key={reason.criterion} direction='row' alignItems='center' spacing={1.5} sx={{ py: 0.75, borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main' }}>
                {criterionIcon(reason.met)}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' sx={{ color: 'text.primary' }}>{reason.label}</Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    Your figure: {formatValue(reason.criterion, reason.value)}
                    {reason.note ? ` (${reason.note})` : ''}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
          {regulation.notes.map(note => (
            <Stack key={note} direction='row' spacing={1} alignItems='flex-start' sx={{ mt: 1.5 }}>
              <InfoOutlined fontSize='small' sx={{ color: 'text.secondary', mt: '2px' }} />
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>{note}</Typography>
            </Stack>
          ))}
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1.5 }}>
            <Link href={regulation.infoUrl} target='_blank' rel='noopener noreferrer'>
              Read the official guidance
            </Link>
          </Typography>
        </Box>
      ))}
      </CustomTabPanel>
      <ComplianceCalendarDialog
        open={calendarDialogOpen}
        onClose={() => setCalendarDialogOpen(false)}
        entries={response?.calendar || []}
        chipLabel={calendarChipLabel}
      />
      <CustomTabPanel value={tab} index={2}>
        {hasLoaded && organisationId && (
          <CertificationsPanel
            holderModelName='organisation'
            holderObjectId={organisationId}
            canEdit={canEditCertifications}
            onChanged={() => setFetchAttempt(a => a + 1)}
          />
        )}
      </CustomTabPanel>
    </PageOuterContainer>
  )
}
